import { useEffect, useState } from "react";
import { CreditCard } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { useChat } from "@/components/chat/ChatProvider";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SpenderFilter, type SpenderScope } from "@/components/SpenderFilter";
import { PageContainer, PageHeader, StatCard } from "@/components/PagePrimitives";
import { EmptyState } from "@/components/PageUi";
import {
  ApprovalsCard,
  CardsPanel,
  CategoryCard,
  CloseReadinessCard,
  LocationCard,
  ReceiptsCard,
  SpendOverTimeCard,
  TopMerchantsPanel,
  TopSpendersPanel,
  VatCard,
  money,
  num,
} from "@/components/ExpenseReportCards";
import { useExpenseReports, type LookbackPeriod } from "@/hooks/useExpenseReports";

const PERIODS: { value: LookbackPeriod; label: string }[] = [
  { value: "Last7Days", label: "Last 7 days" },
  { value: "Last30Days", label: "Last 30 days" },
  { value: "Last90Days", label: "Last 90 days" },
  { value: "MonthToDate", label: "This month" },
  { value: "QuarterToDate", label: "This quarter" },
  { value: "YearToDate", label: "This year" },
  { value: "Last12Months", label: "Last 12 months" },
];

const KPI_LINK =
  "h-full rounded-xl text-left transition hover:[&>div]:border-accent-blue/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue/40 [&>div]:h-full";

export interface ExpensesHomeProps {
  onOpenExpenses: (tab?: string, cardId?: string) => void;
  onOpenMerchant: (merchantId: string) => void;
  onLinkCard: () => void;
}

export function ExpensesHome({ onOpenExpenses, onOpenMerchant, onLinkCard }: ExpensesHomeProps) {
  const api = useDolfinClient();
  const [period, setPeriod] = useState<LookbackPeriod>("Last30Days");
  const [scope, setScope] = useState<SpenderScope>({ kind: "everyone" });
  const reports = useExpenseReports(period, scope);
  const [cardCount, setCardCount] = useState<number | null>(null);
  const { openChat, sendMessage } = useChat();
  const periodLabel = PERIODS.find((p) => p.value === period)?.label.toLowerCase() ?? "";

  useEffect(() => {
    api.GET("/v1/card-connections", {}).then(({ data }) => setCardCount(data?.length ?? 0));
  }, [api]);

  const askAgent = (prompt: string) => {
    openChat();
    void sendMessage(prompt);
  };

  const spend = reports.spend.data;
  const currency = spend?.currency ?? reports.compliance.data?.currency ?? "GBP";
  const total = spend?.totals?.find((t) => t.currency === currency);
  const trend = spend?.trend?.find((t) => t.currency === currency);
  const otherCurrencies = (spend?.totals ?? []).filter((t) => t.currency !== currency).length;
  const receipts = reports.compliance.data?.receipts;
  const missingValue = receipts?.missing?.find((m) => m.currency === currency)?.amount;
  const vat = reports.vat.data;
  const reclaimable = vat?.reclaimable?.find((r) => r.currency === currency)?.amount;
  const atRisk = vat?.estimatedVatAtRisk?.amounts?.find((a) => a.currency === currency)?.amount;
  const pending = reports.approvals.data?.pending;

  const dash = (loading: boolean) => (loading ? "..." : "-");
  const deltaPct = trend?.deltaPercent == null ? null : Math.round(num(trend.deltaPercent) * 100);

  if (cardCount === 0 && !total) {
    return (
      <PageContainer>
        <PageHeader eyebrow="Expenses" title="Overview" />
        <EmptyState
          icon={CreditCard}
          title="Link a card to see your spend"
          action={<Button onClick={onLinkCard}>Link a card</Button>}
        >
          Every transaction on a linked card is captured and coded automatically. Spend, receipts, VAT and
          approvals show up here as soon as the first one lands.
        </EmptyState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Expenses"
        title="Overview"
        subtitle={otherCurrencies > 0 ? `Figures in ${currency}. Spend in ${otherCurrencies} other currenc${otherCurrencies === 1 ? "y" : "ies"} is not included.` : undefined}
        actions={
          <>
            <SpenderFilter value={scope} onChange={setScope} />
            <Select value={period} onValueChange={(v) => setPeriod(v as LookbackPeriod)}>
              <SelectTrigger className="h-9 w-40" aria-label="Period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIODS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @4xl:grid-cols-4">
        <StatCard
          label={`Spend · ${periodLabel}`}
          value={spend ? money(currency, total?.total) : dash(reports.spend.loading)}
          sub={
            deltaPct == null
              ? total && num(total.pendingTotal) > 0
                ? `${money(currency, total.pendingTotal)} still pending`
                : undefined
              : `${deltaPct > 0 ? "+" : ""}${deltaPct}% on the previous period`
          }
          // The arrow belongs to the comparison; with no previous spend there is nothing to point at.
          trend={deltaPct == null ? undefined : trend?.direction === "Up" ? "up" : trend?.direction === "Down" ? "down" : undefined}
          onAskAgent={() => askAgent(`How much have we spent on cards over the ${periodLabel}, on what, and how does it compare with the period before?`)}
        />
        <button type="button" className={KPI_LINK} onClick={() => onOpenExpenses("missing-receipts")}>
          <StatCard
            label="Missing receipts"
            value={receipts ? num(receipts.missingCount) : dash(reports.compliance.loading)}
            sub={receipts ? `${money(currency, missingValue)} of spend` : undefined}
          />
        </button>
        <StatCard
          label="VAT reclaimable"
          value={vat ? money(currency, reclaimable) : dash(reports.vat.loading)}
          sub={vat?.estimatedVatAtRisk && num(atRisk) > 0 ? `≈ ${money(currency, atRisk)} at risk without receipts` : undefined}
          onAskAgent={() => askAgent(`How much VAT can we reclaim on card spend over the ${periodLabel}, and how much are we losing?`)}
        />
        <button type="button" className={KPI_LINK} onClick={() => onOpenExpenses("to-approve")}>
          <StatCard
            label="Awaiting approval"
            value={pending ? num(pending.count) : dash(reports.approvals.loading)}
            sub={pending && num(pending.count) > 0 ? money(currency, pending.byCurrency?.find((c) => c.currency === currency)?.amount) : undefined}
          />
        </button>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-3 @3xl:grid-cols-[3fr_2fr]">
        <SpendOverTimeCard state={reports.spend} />
        <CloseReadinessCard state={reports.compliance} onOpenTab={(tab) => onOpenExpenses(tab)} />
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 @xl:grid-cols-2">
        <CategoryCard state={reports.spend} />
        <TopMerchantsPanel state={reports.spend} onOpenMerchant={onOpenMerchant} />
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 @xl:grid-cols-2 @4xl:grid-cols-3">
        <TopSpendersPanel state={reports.spend} />
        <CardsPanel state={reports.spend} onOpenCard={(id) => onOpenExpenses("all", id)} />
        <LocationCard state={reports.spend} />
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 @xl:grid-cols-3">
        <ReceiptsCard state={reports.compliance} onOpenMissing={() => onOpenExpenses("missing-receipts")} />
        <VatCard state={reports.vat} />
        <ApprovalsCard state={reports.approvals} />
      </div>
    </PageContainer>
  );
}
